import { useState } from 'react';
import { Loader2, Search, SlidersHorizontal, Trash2 } from 'lucide-react';
import {
  useDeletePsaMapping,
  usePsaCompanies,
  usePsaMappings,
  usePutPsaMapping,
  type CustomerSettings,
  type PsaCompany,
  type PsaConnection,
  type PsaMapping,
} from '@/hooks/usePsa.ts';
import { useAuth } from '@/hooks/useAuth.ts';
import { useDivisions } from '@/hooks/useDivisions.ts';
import { apiErrorMessage } from '@/utils/apiError.ts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { CustomerTicketSettings } from './CustomerTicketSettings.tsx';
import { customerSettingsSummary } from './psaSettings.ts';

/**
 * Which company in the PSA each customer is (docs/PsaIntegration.md, "Connecting").
 *
 * A customer is a division. The default row covers every customer without its own,
 * which is the whole setup for an MSP with no divisions yet. A ticket always belongs
 * to one company, so a customer with neither gets no ticket. Each row can also set
 * where that customer's tickets go and their priorities (decision 15).
 */

/** The default row's key in the customer picker; a division is never named this. */
const DEFAULT = '__default';

export function CustomerMappings({ connection, canManage }: { connection: PsaConnection; canManage: boolean }) {
  const { user } = useAuth();
  const divisions = useDivisions(user?.organizationId);
  const mappings = usePsaMappings(connection.id);
  const remove = useDeletePsaMapping(connection.id);
  const [editing, setEditing] = useState<string | null>(null);
  // A division's connection serves that division alone.
  const customers = (divisions.data ?? []).filter((d) => !connection.divisionId || d.id === connection.divisionId);
  const nameOf = (divisionId: string | null) =>
    divisionId ? (customers.find((d) => d.id === divisionId)?.name ?? 'A customer') : 'Every other customer (default)';
  const rows = [...(mappings.data ?? [])].sort((a, b) =>
    a.divisionId === null ? -1 : b.divisionId === null ? 1 : nameOf(a.divisionId).localeCompare(nameOf(b.divisionId)),
  );

  return (
    <div className="space-y-4">
      {mappings.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {rows.length === 0 && !mappings.isLoading ? (
        <p className="text-sm text-muted-foreground">
          No customer is mapped yet, so no ticket can be made. Map the default to send every customer’s
          tickets to one company.
        </p>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-md">
          {rows.map((m) => {
            const own = customerSettingsSummary(m.extra);
            return (
              <li key={m.id} className="px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span>
                    <span className="font-medium">{nameOf(m.divisionId)}</span>
                    <span className="text-muted-foreground"> → {m.companyName}</span>
                    <span className="block text-xs text-muted-foreground">
                      {own ? `Its own ${own}; the rest as the connection’s` : 'Tickets as the connection’s settings'}
                    </span>
                  </span>
                  {canManage ? (
                    <span className="flex shrink-0">
                      <Button variant="ghost" size="sm" aria-expanded={editing === m.id}
                        onClick={() => { setEditing(editing === m.id ? null : m.id); }}>
                        <SlidersHorizontal className="w-3.5 h-3.5" /> Ticket settings
                      </Button>
                      <Button variant="ghost" size="sm" aria-label={`Remove the mapping for ${nameOf(m.divisionId)}`}
                        disabled={remove.isPending} onClick={() => { remove.mutate(m.id); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </span>
                  ) : null}
                </div>
                {editing === m.id ? (
                  <CustomerTicketSettings connection={connection} mapping={m} customerName={nameOf(m.divisionId)}
                    onDone={() => { setEditing(null); }} />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {remove.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(remove.error, 'Couldn’t remove the mapping.')}</p>
      ) : null}
      {canManage ? <MappingForm connection={connection} customers={customers} mappings={mappings.data ?? []} /> : null}
    </div>
  );
}

/**
 * A customer's settings when it moves to another company: where its tickets go and
 * their priorities carry over; the old company's location, contract or site don't.
 */
function carriedOver(existing: PsaMapping | undefined, companyRef: string): CustomerSettings | null {
  if (!existing?.extra) return null;
  if (existing.companyRef === companyRef) return existing.extra;
  const rest = { ...existing.extra };
  delete rest.location_id;
  delete rest.contract_id;
  delete rest.site_id;
  return rest;
}

function MappingForm({
  connection,
  customers,
  mappings,
}: {
  connection: PsaConnection;
  customers: { id: string; name: string }[];
  mappings: PsaMapping[];
}) {
  const [customer, setCustomer] = useState<string>(DEFAULT);
  const [query, setQuery] = useState('');
  const [company, setCompany] = useState<PsaCompany | null>(null);
  const companies = usePsaCompanies(connection.id, query);
  const save = usePutPsaMapping(connection.id);
  const id = (name: string) => `psa-${connection.id}-map-${name}`;

  return (
    <form
      className="space-y-3 border-t border-border pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!company) return;
        const divisionId = customer === DEFAULT ? null : customer;
        const existing = mappings.find((m) => m.divisionId === divisionId);
        save.mutate(
          {
            divisionId,
            companyRef: company.companyRef,
            companyName: company.name,
            extra: carriedOver(existing, company.companyRef),
          },
          { onSuccess: () => { setCompany(null); setQuery(''); } },
        );
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={id('customer')}>Customer</Label>
        <Select value={customer} onValueChange={setCustomer}>
          <SelectTrigger id={id('customer')} className="w-full max-w-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT}>Every other customer (default)</SelectItem>
            {customers.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={id('search')}>Company in {connection.name}</Label>
        <div className="relative max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input id={id('search')} className="pl-8" placeholder="Search by name" value={query}
            onChange={(e) => { setQuery(e.target.value); setCompany(null); }} />
        </div>
        {companies.isFetching ? <p className="text-xs text-muted-foreground">Searching…</p> : null}
        {companies.isError ? (
          <p className="text-xs text-destructive">{apiErrorMessage(companies.error, 'The search failed.')}</p>
        ) : null}
        {!company && companies.data ? (
          companies.data.length === 0 ? (
            <p className="text-xs text-muted-foreground">No active company matches.</p>
          ) : (
            <ul className="max-w-sm border border-border rounded-md divide-y divide-border text-sm">
              {companies.data.slice(0, 10).map((c) => (
                <li key={c.companyRef}>
                  <button type="button" className="w-full text-left px-3 py-1.5 hover:bg-accent"
                    onClick={() => { setCompany(c); setQuery(c.name); }}>
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>
      {save.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(save.error, 'Couldn’t save the mapping.')}</p>
      ) : null}
      <Button type="submit" size="sm" disabled={!company || save.isPending}>
        {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Save mapping
      </Button>
    </form>
  );
}
